import jsPDF from 'jspdf'
import autoTable, { type RowInput } from 'jspdf-autotable'
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'
import useFileHandler from '@/composables/useFileHandler'
import {
	tintRgb,
	stripHtml,
	sanitizeFilenamePart,
	imageMetaFromDataUrl,
	loadLogoDataUrl,
	resolvePrimaryRgb,
	collectPdfMetadata,
} from '@/helpers/pdf'
import type { DataTableHeader, SortItem } from '@/types/table'

export interface ExportFilterDescriptor {
	label: string
	value: string
}

export interface ExportGroup {
	title: string
	rows: any[]
}

export interface ExportContextItem {
	label: string
	value: string
}

export interface ExportSummaryRow {
	label: string
	value: string
	emphasized?: boolean
}

export interface ExportSummary {
	title?: string
	rows: ExportSummaryRow[]
	hint?: string
}

export interface ExportInput {
	title: string
	headers: DataTableHeader[]
	rows?: any[]
	groups?: ExportGroup[]
	activeFilters?: ExportFilterDescriptor[]
	searchTerm?: string
	context?: ExportContextItem[]
	summary?: ExportSummary
	includeNotes?: boolean
	/**
	 * Extra identifying bits appended to the PDF filename after the title.
	 * Each non-empty piece is sanitized and joined with underscores, e.g.
	 *   title=Personenfunktionen, filenameContext=[2812, "Mustermann-Max"]
	 *   → Personenfunktionen_2812_Mustermann-Max_2026-05-27.pdf
	 */
	filenameContext?: ReadonlyArray<string | number | null | undefined>
	/**
	 * For collections that wrap a parent row around one or more child rows
	 * (definition.multiple.linkItem in the DataTable config). When a header
	 * sets hasMultiple, cell resolution reads from `item[linkItem][0]`
	 * instead of `item` directly — mirrors TableCell.vue's browser behavior.
	 */
	linkItem?: string
	/**
	 * Columns of the expand-row subtable (definition.multiple.columns) with
	 * titles already translated. Required when includeSubtables is true.
	 */
	subHeaders?: DataTableHeader[]
	/**
	 * When true and a row has sub-items at item[linkItem], the PDF inserts
	 * one continuation row per sub-item summarising it using subHeaders.
	 */
	includeSubtables?: boolean
	/**
	 * For grouped exports (input.groups): controls page-break behavior between groups.
	 * - 'always': every group starts on a new page
	 * - 'auto'  (default): groups flow on the same page, but if a group's title +
	 *   table header + first body rows wouldn't fit on the remaining page, it is
	 *   pushed to a new page to avoid an orphaned title
	 */
	groupPageBreak?: 'always' | 'auto'
}

function pickByPath(item: any, path: string): unknown {
	if (!path) return undefined
	return path.split('.').reduce<any>((acc, key) => {
		if (acc === null || typeof acc === 'undefined') return undefined
		return acc[key]
	}, item)
}

// Mirrors TableCell.vue:240-249 ifNull fallback semantics.
// - string[]: resolve each path, drop empties, join with keyJoin (same as pickKey array)
// - string:   resolve path; if nullish, fall back to the literal string (UI uses `??=`)
function resolveIfNull(source: any, ifNull: unknown, keyJoin: string): string {
	if (Array.isArray(ifNull)) {
		return ifNull
			.map((k) => pickByPath(source, String(k)))
			.filter((v) => v !== undefined && v !== null && v !== '')
			.map((v) => stringifyCell(v))
			.join(keyJoin)
	}
	if (typeof ifNull === 'string') {
		const v = pickByPath(source, ifNull)
		if (v === undefined || v === null) return ifNull
		return stringifyCell(v)
	}
	return ''
}

type IconMarker = 'check' | 'cross' | 'warn' | 'wait' | 'info' | 'lock'

const ICON_MARKER_PREFIX = '@@FA::'
const ICON_MARKER_SUFFIX = '@@'

function makeIconMarker(kind: IconMarker): string {
	return `${ICON_MARKER_PREFIX}${kind}${ICON_MARKER_SUFFIX}`
}

function parseIconMarker(text: string): IconMarker | null {
	if (!text || !text.startsWith(ICON_MARKER_PREFIX)) return null
	if (!text.endsWith(ICON_MARKER_SUFFIX)) return null
	const kind = text.slice(ICON_MARKER_PREFIX.length, -ICON_MARKER_SUFFIX.length)
	if (
		kind === 'check' ||
		kind === 'cross' ||
		kind === 'warn' ||
		kind === 'wait' ||
		kind === 'info' ||
		kind === 'lock'
	) {
		return kind
	}
	return null
}

const ICON_CLASS_TO_MARKER: Array<[RegExp, IconMarker]> = [
	[/\bfa-(check|check-circle|check-square|thumbs-up)\b/i, 'check'],
	[/\bfa-(times|times-circle|ban|xmark|x|window-close|thumbs-down)\b/i, 'cross'],
	[/\bfa-(exclamation|exclamation-triangle|exclamation-circle|warning)\b/i, 'warn'],
	[/\bfa-(hourglass(-start|-half|-end)?|clock)\b/i, 'wait'],
	[/\bfa-(question(-circle)?|info(-circle)?)\b/i, 'info'],
	[/\bfa-(lock|key)\b/i, 'lock'],
]

function iconClassToMarker(cssClass: string): string {
	for (const [pattern, kind] of ICON_CLASS_TO_MARKER) {
		if (pattern.test(cssClass)) return makeIconMarker(kind)
	}
	return ''
}

function drawIconGlyph(
	doc: jsPDF,
	kind: IconMarker,
	cx: number,
	cy: number,
	size: number
) {
	const r = size / 2
	const lw = Math.max(1, size * 0.14)
	doc.setLineWidth(lw)
	doc.setLineCap('round')
	doc.setLineJoin('round')

	switch (kind) {
		case 'check':
			doc.setDrawColor(34, 167, 93) // green
			doc.line(cx - r * 0.55, cy + r * 0.05, cx - r * 0.1, cy + r * 0.5)
			doc.line(cx - r * 0.1, cy + r * 0.5, cx + r * 0.6, cy - r * 0.45)
			break
		case 'cross':
			doc.setDrawColor(220, 53, 69) // red
			doc.line(cx - r * 0.5, cy - r * 0.5, cx + r * 0.5, cy + r * 0.5)
			doc.line(cx + r * 0.5, cy - r * 0.5, cx - r * 0.5, cy + r * 0.5)
			break
		case 'warn':
			doc.setDrawColor(230, 145, 40) // orange
			doc.setFillColor(252, 224, 169)
			doc.triangle(
				cx, cy - r * 0.7,
				cx - r * 0.7, cy + r * 0.55,
				cx + r * 0.7, cy + r * 0.55,
				'FD'
			)
			doc.setDrawColor(180, 100, 20)
			doc.setFillColor(180, 100, 20)
			doc.circle(cx, cy + r * 0.4, lw * 0.4, 'F')
			doc.line(cx, cy - r * 0.2, cx, cy + r * 0.2)
			break
		case 'wait':
			doc.setDrawColor(120, 120, 120) // grey
			doc.line(cx - r * 0.5, cy - r * 0.55, cx + r * 0.5, cy - r * 0.55)
			doc.line(cx - r * 0.5, cy + r * 0.55, cx + r * 0.5, cy + r * 0.55)
			doc.line(cx - r * 0.5, cy - r * 0.55, cx + r * 0.5, cy + r * 0.55)
			doc.line(cx + r * 0.5, cy - r * 0.55, cx - r * 0.5, cy + r * 0.55)
			break
		case 'info':
			doc.setDrawColor(46, 117, 200) // blue
			doc.setFillColor(46, 117, 200)
			doc.circle(cx, cy, r * 0.7, 'S')
			doc.setFontSize(size * 0.85)
			doc.setFont('helvetica', 'bold')
			doc.setTextColor(46, 117, 200)
			doc.text('i', cx, cy + r * 0.32, { align: 'center' })
			break
		case 'lock':
			doc.setDrawColor(110, 110, 110) // grey
			doc.setFillColor(110, 110, 110)
			doc.rect(cx - r * 0.45, cy - r * 0.05, r * 0.9, r * 0.6, 'F')
			doc.setLineWidth(lw * 0.8)
			doc.line(cx - r * 0.3, cy - r * 0.05, cx - r * 0.3, cy - r * 0.4)
			doc.line(cx + r * 0.3, cy - r * 0.05, cx + r * 0.3, cy - r * 0.4)
			doc.line(cx - r * 0.3, cy - r * 0.4, cx + r * 0.3, cy - r * 0.4)
			break
	}

	// reset
	doc.setLineWidth(0.2)
	doc.setLineCap('butt')
	doc.setLineJoin('miter')
}

function stringifyCell(value: unknown): string {
	if (value === null || typeof value === 'undefined') return '-'
	if (typeof value === 'string') return stripHtml(value)
	if (typeof value === 'number') return String(value)
	if (typeof value === 'boolean') return makeIconMarker(value ? 'check' : 'cross')
	if (value instanceof Date) return dayjs(value).format('DD.MM.YYYY HH:mm')
	if (typeof value === 'object') {
		const obj = value as Record<string, any>
		const type = typeof obj.type === 'string' ? obj.type : undefined

		if (type === 'icon' || type === 'tosAndPolicy') {
			return typeof obj.value === 'string' ? iconClassToMarker(obj.value) : ''
		}
		if (type === 'iconText') {
			return typeof obj.text === 'string' ? obj.text : ''
		}
		if (type === 'badge') {
			if (typeof obj.value === 'object' && obj.value !== null) {
				return String(obj.value.text ?? obj.value.name ?? '')
			}
			return ''
		}
		if (type === 'avatar' || type === 'selectRow' || type === 'expand') {
			return ''
		}

		if (typeof obj.value === 'string') return stripHtml(obj.value)
		if (typeof obj.value === 'number') return String(obj.value)
		if (typeof obj.text === 'string') return stripHtml(obj.text)
		if (typeof obj.name === 'string') return stripHtml(obj.name)
		if (typeof obj.title === 'string') return stripHtml(obj.title)
		return ''
	}
	return String(value)
}

function resolveCellValue(
	item: any,
	header: DataTableHeader,
	linkItem?: string
): string {
	// Mirrors TableCell.vue: for hasMultiple columns, read from item[linkItem][0]
	// instead of the parent row. Users.persons[0] is the actual data carrier.
	const source =
		header.hasMultiple && linkItem && Array.isArray(item?.[linkItem])
			? item[linkItem][0] ?? item
			: item

	if (typeof header.value === 'function') {
		const computed = header.value(source)
		return header.format ? stringifyCell(header.format(computed)) : stringifyCell(computed)
	}

	const pickKey = header.pickKey
	const keyJoin = header.keyJoin ?? ' '

	if (Array.isArray(pickKey) && pickKey.length > 0) {
		const parts = pickKey
			.map((p) => pickByPath(source, p))
			.filter((v) => v !== undefined && v !== null && v !== '')
			.map((v) => stringifyCell(v))
		let joined = parts.join(keyJoin)
		if (!joined && header.ifNull !== undefined) {
			joined = resolveIfNull(source, header.ifNull, keyJoin)
		}
		if (header.format) {
			return stringifyCell(header.format(joined))
		}
		return joined || '-'
	}

	if (typeof pickKey === 'string' && pickKey.length > 0) {
		let raw: unknown = pickByPath(source, pickKey)
		if ((raw === undefined || raw === null || raw === '') && header.ifNull !== undefined) {
			raw = resolveIfNull(source, header.ifNull, keyJoin)
		}
		return header.format ? stringifyCell(header.format(raw)) : stringifyCell(raw)
	}

	let raw: unknown = pickByPath(source, header.key)
	if ((raw === undefined || raw === null || raw === '') && header.ifNull !== undefined) {
		raw = resolveIfNull(source, header.ifNull, keyJoin)
	}
	if (header.format) {
		return stringifyCell(header.format(raw))
	}
	return stringifyCell(raw)
}

// Composes <title>_<ctxBits...>_<YYYY-MM-DD>.<ext>. Each context piece is
// sanitized independently; empty/nullish pieces are dropped so missing
// entity data never produces dangling underscores.
function buildExportFilename(
	title: string,
	ctx: ReadonlyArray<string | number | null | undefined> | undefined,
	ext: 'pdf' | 'csv' = 'pdf',
	now: dayjs.Dayjs = dayjs()
): string {
	const parts: string[] = []
	const sanitizedTitle = sanitizeFilenamePart(title || 'Export')
	if (sanitizedTitle) parts.push(sanitizedTitle)
	if (ctx) {
		for (const piece of ctx) {
			if (piece === null || piece === undefined || piece === '') continue
			const sanitized = sanitizeFilenamePart(String(piece))
			if (sanitized) parts.push(sanitized)
		}
	}
	parts.push(now.format('YYYY-MM-DD'))
	return `${parts.join('_')}.${ext}`
}

function alignToHalign(
	a?: 'start' | 'end' | 'center'
): 'left' | 'right' | 'center' | undefined {
	if (a === 'end') return 'right'
	if (a === 'center') return 'center'
	if (a === 'start') return 'left'
	return undefined
}

export function describeActiveFilters(
	filters: Array<{ definition?: { name?: string; component?: string }; value?: any }> | undefined,
	sortByValues: SortItem[] | undefined,
	t: (key: string, fallback?: string) => string
): ExportFilterDescriptor[] {
	const out: ExportFilterDescriptor[] = []

	for (const filter of filters || []) {
		const value = filter?.value
		if (value === undefined || value === null || value === '') continue
		if (Array.isArray(value) && value.length === 0) continue

		const label = filter.definition?.name || filter.definition?.component || ''
		if (!label) continue

		let display: string
		if (Array.isArray(value)) {
			display = value
				.map((v) =>
					typeof v === 'object' && v !== null
						? String(v.name ?? v.title ?? v.label ?? v.id ?? '')
						: String(v)
				)
				.filter(Boolean)
				.join(', ')
		} else if (typeof value === 'object') {
			display = String(
				(value as any).name ?? (value as any).title ?? (value as any).label ?? (value as any).id ?? ''
			)
		} else {
			display = String(value)
		}

		if (display) out.push({ label, value: display })
	}

	if (sortByValues && sortByValues.length > 0) {
		const parts = sortByValues
			.map((s) => `${s.key} (${s.order === 'desc' ? 'desc' : 'asc'})`)
			.join(', ')
		out.push({ label: t('component.export.sort', 'Sortierung'), value: parts })
	}

	return out
}

export function useTableExport() {
	const { t, locale } = useI18n()
	const { downloadBlob } = useFileHandler()

	const NON_EXPORTABLE_KEYS = new Set([
		'data-table-select',
		'data-table-expand',
		'select',
		'expand',
		'actions',
	])
	const NON_EXPORTABLE_TYPES = new Set([
		'selectRow',
		'expand',
		'avatar',
		'iconDocumentContainerDialog',
	])

	function buildHeaderTitles(headers: DataTableHeader[]): { columns: DataTableHeader[]; titles: string[] } {
		const exportable = headers.filter((h) => {
			if (!h.key) return false
			if (NON_EXPORTABLE_KEYS.has(h.key)) return false
			if (h.type && NON_EXPORTABLE_TYPES.has(h.type)) return false
			return true
		})
		const titles = exportable.map((h) => (typeof h.title === 'string' ? h.title : h.key))
		return { columns: exportable, titles }
	}

	function rowToCells(
		item: any,
		columns: DataTableHeader[],
		linkItem?: string
	): string[] {
		return columns.map((col) => resolveCellValue(item, col, linkItem))
	}

	async function exportToPdf(input: ExportInput): Promise<void> {
		const { columns, titles } = buildHeaderTitles(input.headers)

		if (columns.length === 0) {
			throw new Error('No exportable columns')
		}

		const noteHeader = input.includeNotes ? t('component.export.notesColumn', 'Notiz') : null
		const titlesWithNotes = noteHeader ? [...titles, noteHeader] : titles

		const totalCols = titlesWithNotes.length
		const orientation = totalCols > 6 ? 'landscape' : 'portrait'
		const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' })

		const primaryRgb = resolvePrimaryRgb()
		const altRowRgb = tintRgb(primaryRgb)

		const pageWidth = doc.internal.pageSize.getWidth()
		const pageHeight = doc.internal.pageSize.getHeight()
		const margin = 32
		const generatedAt = dayjs().locale(locale.value || 'de').format('DD.MM.YYYY HH:mm')
		const meta = collectPdfMetadata()

		const logoDataUrl = await loadLogoDataUrl()
		const logoBoxW = 90
		const logoBoxH = 32

		if (logoDataUrl) {
			try {
				const imgMeta = imageMetaFromDataUrl(logoDataUrl)
				doc.addImage(logoDataUrl, imgMeta.format, margin, margin - 6, logoBoxW, logoBoxH, undefined, 'FAST')
			} catch {
				// silently skip logo on render failure
			}
		}

		doc.setFont('helvetica', 'bold')
		doc.setFontSize(16)
		doc.setTextColor(...primaryRgb)
		doc.text(input.title.toUpperCase(), pageWidth - margin, margin + 8, { align: 'right' })

		doc.setDrawColor(...primaryRgb)
		doc.setLineWidth(1.2)
		const dividerY = margin + logoBoxH + 4
		doc.line(margin, dividerY, pageWidth - margin, dividerY)

		let headerBottomY = dividerY + 4

		// Context block (e.g. account header for journal exports) — sits between the
		// title divider and the filter line. Rendered as a labelled key/value list.
		if (input.context && input.context.length > 0) {
			doc.setDrawColor(220, 220, 220)
			doc.setLineWidth(0.5)
			doc.line(margin, headerBottomY + 2, pageWidth - margin, headerBottomY + 2)
			headerBottomY += 4

			const ctxLineHeight = 11
			const ctxColGap = 18
			const ctxCols = 2
			const colWidth = (pageWidth - 2 * margin - (ctxCols - 1) * ctxColGap) / ctxCols
			const labelMutedRgb: [number, number, number] = [120, 120, 120]
			const valueRgb: [number, number, number] = [40, 40, 40]

			doc.setFontSize(9)
			const ctxY = headerBottomY + ctxLineHeight
			for (let i = 0; i < input.context.length; i++) {
				const colIdx = i % ctxCols
				const rowIdx = Math.floor(i / ctxCols)
				const x = margin + colIdx * (colWidth + ctxColGap)
				const y = ctxY + rowIdx * ctxLineHeight

				doc.setFont('helvetica', 'bold')
				doc.setTextColor(...labelMutedRgb)
				const label = `${input.context[i].label}:`
				doc.text(label, x, y)
				const labelWidth = doc.getTextWidth(`${label} `)

				doc.setFont('helvetica', 'normal')
				doc.setTextColor(...valueRgb)
				const valueMaxWidth = colWidth - labelWidth
				const valueLines = doc.splitTextToSize(
					input.context[i].value || '-',
					Math.max(20, valueMaxWidth)
				)
				doc.text(valueLines[0] ?? '-', x + labelWidth, y)
			}
			const ctxRows = Math.ceil(input.context.length / ctxCols)
			headerBottomY = ctxY + (ctxRows - 1) * ctxLineHeight + 4
		}

		const filterLines: string[] = []
		if (input.searchTerm && input.searchTerm.trim().length > 0) {
			filterLines.push(
				`${t('component.export.searchTerm', 'Suche')}: ${input.searchTerm.trim()}`
			)
		}
		if (input.activeFilters && input.activeFilters.length > 0) {
			const filterText = input.activeFilters
				.map((f) => `${f.label}: ${f.value}`)
				.join('   ·   ')
			filterLines.push(
				`${t('component.export.activeFilters', 'Filter')}: ${filterText}`
			)
		}

		if (filterLines.length > 0) {
			doc.setDrawColor(220, 220, 220)
			doc.setLineWidth(0.5)
			doc.line(margin, headerBottomY + 2, pageWidth - margin, headerBottomY + 2)
			headerBottomY += 8

			doc.setFontSize(8)
			doc.setFont('helvetica', 'normal')
			doc.setTextColor(80, 80, 80)
			let textY = headerBottomY + 8
			for (const line of filterLines) {
				const wrapped = doc.splitTextToSize(line, pageWidth - 2 * margin)
				doc.text(wrapped, margin, textY)
				textY += wrapped.length * 10
			}
			headerBottomY = textY - 8
		}

		let startY = headerBottomY + 8
		const groupsToRender: ExportGroup[] = input.groups?.length
			? input.groups
			: [{ title: '', rows: input.rows ?? [] }]

		const subEnabled =
			!!input.includeSubtables &&
			!!input.linkItem &&
			Array.isArray(input.subHeaders) &&
			input.subHeaders.length > 0

		// Pre-render all rows once so we can size parent columns uniformly across
		// all groups. Subtable rows are NOT included here — they get their own
		// nested autoTable call per parent.
		const groupBodies: RowInput[][] = groupsToRender.map((g) =>
			g.rows.map((item) => {
				const cells = rowToCells(item, columns, input.linkItem)
				return noteHeader ? [...cells, ''] : cells
			})
		)

		// Compute one column-width array shared by every group's autoTable.
		// Strategy:
		//   1. Measure max content width per column (header + every body cell across all groups)
		//      using current body font + cell padding.
		//   2. Clamp each width to [minColWidth, maxColWidth].
		//   3. If total fits the page, distribute the leftover proportionally so the table
		//      always spans the full content width — keeping every group identical.
		//   4. Reserve a fixed wider slot for the optional notes column.
		const sharedColumnStyles = (() => {
			const fontSize = 8
			const cellPadding = 4
			const slack = 6 // extra px per cell for safety vs. measurement vs. linebreak
			const minColWidth = 28
			const maxColWidth = 220
			const notesWidth = 120

			doc.setFont('helvetica', 'normal')
			doc.setFontSize(fontSize)
			const measure = (s: string): number =>
				doc.getStringUnitWidth(s) * fontSize

			const dataColCount = titles.length
			const desired: number[] = new Array(dataColCount).fill(0)

			for (let i = 0; i < dataColCount; i++) {
				let max = measure(titles[i])
				for (const body of groupBodies) {
					for (const row of body) {
						const cell = (row as any[])[i]
						// Skip colSpan-bearing cells (sub-table continuation rows) —
						// their content stretches across all columns and would otherwise
						// blow up the width of column 0.
						if (
							cell !== null &&
							typeof cell === 'object' &&
							(cell as any).colSpan
						) {
							continue
						}
						const text = typeof cell === 'string' ? cell : String(cell ?? '')
						// account for linebreaks introduced by autoTable's overflow:'linebreak'
						const widest = text.split(/\r?\n/).reduce(
							(acc, line) => Math.max(acc, measure(line)),
							0
						)
						if (widest > max) max = widest
					}
				}
				desired[i] = Math.min(maxColWidth, Math.max(minColWidth, max + cellPadding * 2 + slack))
			}

			const available =
				pageWidth - 2 * margin - (noteHeader ? notesWidth : 0)
			const total = desired.reduce((s, w) => s + w, 0)

			const final = desired.slice()
			if (total < available) {
				// Distribute extra space proportionally so the table fills the page width.
				const extra = available - total
				for (let i = 0; i < dataColCount; i++) {
					final[i] = desired[i] + (desired[i] / total) * extra
				}
			} else if (total > available) {
				// Shrink proportionally (autoTable's linebreak will wrap content).
				const scale = available / total
				for (let i = 0; i < dataColCount; i++) {
					final[i] = Math.max(minColWidth, desired[i] * scale)
				}
			}

			const styles: Record<
				number,
				{ cellWidth: number; minCellHeight?: number; halign?: 'left' | 'right' | 'center' }
			> = {}
			for (let i = 0; i < dataColCount; i++) {
				const halign = alignToHalign(columns[i]?.align)
				styles[i] = { cellWidth: final[i], ...(halign ? { halign } : {}) }
			}
			if (noteHeader) {
				styles[dataColCount] = { cellWidth: notesWidth, minCellHeight: 28 }
			}
			return styles
		})()

		const groupedExport = (input.groups?.length ?? 0) > 0
		const groupBreakMode: 'always' | 'auto' = input.groupPageBreak ?? 'auto'
		// keep in sync with the footer-rendering loop below (footerSepY etc.)
		const footerReserveHeight = margin / 2 + 18 + 12
		const minGroupBlockHeight = 80 // group title + table header + ~2 body rows

		for (let groupIndex = 0; groupIndex < groupsToRender.length; groupIndex++) {
			const group = groupsToRender[groupIndex]
			if (groupedExport && groupIndex > 0) {
				const remainingSpace = pageHeight - footerReserveHeight - startY
				const shouldBreak =
					groupBreakMode === 'always' || remainingSpace < minGroupBlockHeight
				if (shouldBreak) {
					doc.addPage()
					startY = margin
				}
			}
			if (group.title) {
				doc.setFont('helvetica', 'bold')
				doc.setFontSize(11)
				doc.setTextColor(...primaryRgb)
				doc.text(group.title, margin, startY)
				startY += 6
			}

			const body: RowInput[] = groupBodies[groupIndex]

			const columnStyles = sharedColumnStyles

			// Build head cells with explicit halign per column so the title
			// inherits the same alignment as the body (jspdf-autotable does not
			// always cascade columnStyles.halign onto the head section).
			const headRow = titlesWithNotes.map((title, i) => {
				const halign = (columnStyles as any)[i]?.halign
				return halign ? { content: title, styles: { halign } } : title
			})

			// Body-cell icon hooks: replace marker text with drawn glyph so check/cross
			// columns render their fa-icon visually in both parent and sub-tables.
			const iconWillDraw = (data: any) => {
				if (data.section !== 'body') return
				const raw = (data.row.raw as any[])?.[data.column.index]
				if (typeof raw === 'string' && parseIconMarker(raw)) {
					data.cell.text = ['']
				}
			}
			const iconDidDraw = (data: any) => {
				if (data.section !== 'body') return
				const raw = (data.row.raw as any[])?.[data.column.index]
				const rawStr = typeof raw === 'string' ? raw : ''
				const kind = parseIconMarker(rawStr)
				if (!kind) return
				const cx = data.cell.x + data.cell.width / 2
				const cy = data.cell.y + data.cell.height / 2
				const size = Math.min(11, data.cell.width - 4, data.cell.height - 4)
				drawIconGlyph(doc, kind, cx, cy, size)
			}

			if (!subEnabled) {
				// Single-autoTable path: original behavior, all parent rows in one shot.
				autoTable(doc, {
					head: [headRow],
					body,
					startY: startY + 4,
					margin: { left: margin, right: margin },
					styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
					headStyles: {
						fillColor: primaryRgb,
						textColor: 255,
						fontStyle: 'bold',
					},
					alternateRowStyles: { fillColor: altRowRgb },
					columnStyles,
					theme: 'grid',
					willDrawCell: iconWillDraw,
					didDrawCell: iconDidDraw,
				})
			} else {
				// Subtable path: per-parent rendering so each parent gets its own
				// nested sub-table directly below it (indented). Header is drawn
				// once alongside the first parent row.
				const subEntity =
					(input.linkItem as string | undefined)?.replace(/s$/, '') || 'sub'
				const subTitles = (input.subHeaders ?? []).map((h) =>
					typeof h.title === 'string' && h.title.length > 0 ? h.title : h.key
				)
				const subTitlesWithNotes = noteHeader
					? [...subTitles, noteHeader]
					: subTitles
				const subIndent = 20

				let currentY = startY + 4
				for (let i = 0; i < group.rows.length; i++) {
					const item = group.rows[i]
					const parentRow = body[i]
					autoTable(doc, {
						head: i === 0 ? [headRow] : undefined,
						body: [parentRow as any],
						startY: currentY,
						margin: { left: margin, right: margin },
						styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
						headStyles: {
							fillColor: primaryRgb,
							textColor: 255,
							fontStyle: 'bold',
						},
						bodyStyles:
							i % 2 === 1 ? { fillColor: altRowRgb } : undefined,
						columnStyles,
						theme: 'grid',
						willDrawCell: iconWillDraw,
						didDrawCell: iconDidDraw,
					})
					currentY = (doc as any).lastAutoTable?.finalY ?? currentY

					const subItems =
						(item?.[input.linkItem as string] as any[]) || []
					if (subItems.length > 0 && input.subHeaders?.length) {
						const subBody = subItems.map((s) => {
							const cells = input.subHeaders!.map((h) =>
								resolveCellValue(s, h)
							)
							return noteHeader ? [...cells, ''] : cells
						})
						autoTable(doc, {
							head: [subTitlesWithNotes],
							body: subBody,
							startY: currentY,
							margin: { left: margin + subIndent, right: margin },
							styles: {
								fontSize: 7,
								cellPadding: 3,
								overflow: 'linebreak',
							},
							headStyles: {
								fillColor: [220, 220, 220],
								textColor: 30,
								fontStyle: 'bold',
							},
							theme: 'grid',
							willDrawCell: iconWillDraw,
							didDrawCell: iconDidDraw,
						})
						currentY = (doc as any).lastAutoTable?.finalY ?? currentY
					}
				}
				// silence unused-var warning when subEntity isn't read elsewhere
				void subEntity
			}

			startY = (doc as any).lastAutoTable?.finalY + 18 || startY + 40
		}

		// Summary block (e.g. Σ Soll / Σ Haben / Endsaldo for journal exports) —
		// rendered as a right-aligned key/value list below the last group. Pushed to
		// a new page if it wouldn't fit alongside the page footer.
		if (input.summary && input.summary.rows.length > 0) {
			const summaryLineHeight = 12
			const summaryTitleHeight = input.summary.title ? 14 : 0
			const summaryHintHeight = input.summary.hint ? 12 : 0
			const summaryHeight =
				summaryTitleHeight +
				input.summary.rows.length * summaryLineHeight +
				summaryHintHeight +
				6
			if (pageHeight - footerReserveHeight - startY < summaryHeight) {
				doc.addPage()
				startY = margin
			}

			const blockWidth = 230
			const blockX = pageWidth - margin - blockWidth
			let summaryY = startY

			if (input.summary.title) {
				doc.setFont('helvetica', 'bold')
				doc.setFontSize(10)
				doc.setTextColor(...primaryRgb)
				doc.text(input.summary.title, blockX, summaryY + 10)
				summaryY += summaryTitleHeight
				doc.setDrawColor(...primaryRgb)
				doc.setLineWidth(0.6)
				doc.line(blockX, summaryY, pageWidth - margin, summaryY)
				summaryY += 4
			}

			const labelMuted: [number, number, number] = [110, 110, 110]
			const valueRgb: [number, number, number] = [40, 40, 40]
			for (const row of input.summary.rows) {
				const isBold = row.emphasized === true
				doc.setFont('helvetica', isBold ? 'bold' : 'normal')
				doc.setFontSize(isBold ? 10 : 9)
				doc.setTextColor(...labelMuted)
				doc.text(`${row.label}:`, blockX, summaryY + 9)
				doc.setTextColor(...valueRgb)
				doc.text(row.value, pageWidth - margin, summaryY + 9, {
					align: 'right',
				})
				summaryY += summaryLineHeight
			}

			if (input.summary.hint) {
				doc.setFont('helvetica', 'italic')
				doc.setFontSize(7)
				doc.setTextColor(140, 140, 140)
				doc.text(input.summary.hint, pageWidth - margin, summaryY + 8, {
					align: 'right',
				})
				summaryY += summaryHintHeight
			}

			startY = summaryY + 6
		}

		const pageCount = doc.getNumberOfPages()
		const tenantLabel = t('component.export.metadata.tenant', 'Mandant')
		const userLabel = t('component.export.metadata.user', 'Benutzer')
		const pathLabel = t('component.export.metadata.path', 'Pfad')
		const generatedAtLabel = t('component.export.generatedAt', 'Erstellt am')

		const footerLineGap = 10
		const footerBottomPad = margin / 2
		const footerLine2Y = pageHeight - footerBottomPad
		const footerLine1Y = footerLine2Y - footerLineGap
		const footerSepY = footerLine1Y - 8

		const labelMutedColor: [number, number, number] = [150, 150, 150]
		const valueColor: [number, number, number] = [80, 80, 80]

		const writeFooterPair = (label: string, value: string, x: number, y: number) => {
			doc.setFont('helvetica', 'bold')
			doc.setTextColor(...labelMutedColor)
			doc.text(`${label}:`, x, y)
			const labelWidth = doc.getTextWidth(`${label}: `)
			doc.setFont('helvetica', 'normal')
			doc.setTextColor(...valueColor)
			doc.text(value, x + labelWidth, y)
		}

		for (let i = 1; i <= pageCount; i++) {
			doc.setPage(i)
			doc.setDrawColor(...primaryRgb)
			doc.setLineWidth(0.6)
			doc.line(margin, footerSepY, pageWidth - margin, footerSepY)

			doc.setFontSize(7)

			writeFooterPair(tenantLabel, meta.tenant, margin, footerLine1Y)
			const midX = pageWidth / 2 + 10
			writeFooterPair(userLabel, meta.user, midX, footerLine1Y)

			writeFooterPair(pathLabel, meta.path, margin, footerLine2Y)
			writeFooterPair(generatedAtLabel, generatedAt, midX, footerLine2Y)

			const text = t('component.export.pageOfPages', {
				current: i,
				total: pageCount,
			})
			doc.setFont('helvetica', 'normal')
			doc.setTextColor(...valueColor)
			doc.text(text, pageWidth - margin, footerLine2Y, { align: 'right' })
		}

		try {
			doc.setProperties({
				title: input.title,
				author: meta.user,
				subject: input.title,
				keywords: [meta.tenant, meta.path].filter(Boolean).join(', '),
				creator: 'Aaremo',
			})
		} catch {
			// older jsPDF versions may not support all fields
		}

		const filename = buildExportFilename(input.title, input.filenameContext, 'pdf')
		const blob = doc.output('blob')
		downloadBlob(blob, filename)
	}

	// --- CSV export ---------------------------------------------------------
	// Consumes the same format-neutral ExportInput as exportToPdf. Produces a
	// flat, spreadsheet-friendly table (header row + data rows). German Excel
	// dialect: ';' delimiter + UTF-8 BOM so a double-click opens with proper
	// columns and umlauts. PDF-only options (notes column, page breaks, nested
	// subtables) do not apply and are ignored.
	const CSV_SEP = ';'

	// resolveCellValue() yields icon markers (@@FA::check@@ …) for booleans and
	// icon columns — meaningless in a spreadsheet. Map them to localized text;
	// non-textual status icons (warn/wait/info/lock) collapse to empty.
	function markerToCsvText(cell: string): string {
		const kind = parseIconMarker(cell)
		if (kind === 'check') return t('component.export.boolean.yes', 'Ja')
		if (kind === 'cross') return t('component.export.boolean.no', 'Nein')
		if (kind) return ''
		return cell
	}

	// RFC 4180 escaping. Embedded newlines are normalized to \n and the field is
	// quoted, so multi-line free text (notes, descriptions) stays inside ONE
	// cell and never shifts the row/column structure. ';' and '"' are quoted too.
	function csvEscape(value: string): string {
		const normalized = value.replace(/\r\n?/g, '\n')
		if (/[";\n]/.test(normalized)) {
			return `"${normalized.replace(/"/g, '""')}"`
		}
		return normalized
	}

	function csvRow(cells: string[]): string {
		return cells.map((c) => csvEscape(markerToCsvText(c))).join(CSV_SEP)
	}

	async function exportToCsv(input: ExportInput): Promise<void> {
		const { columns, titles } = buildHeaderTitles(input.headers)

		if (columns.length === 0) {
			throw new Error('No exportable columns')
		}

		const grouped = (input.groups?.length ?? 0) > 0
		const groupLabel = t('component.export.groupColumn', 'Gruppe')
		const headerCells = grouped ? [groupLabel, ...titles] : titles

		const lines: string[] = [csvRow(headerCells)]

		const renderRows = (rows: any[], groupTitle?: string) => {
			for (const item of rows) {
				const cells = rowToCells(item, columns, input.linkItem)
				lines.push(csvRow(groupTitle !== undefined ? [groupTitle, ...cells] : cells))
			}
		}

		if (grouped) {
			for (const group of input.groups ?? []) {
				renderRows(group.rows, group.title ?? '')
			}
		} else {
			renderRows(input.rows ?? [])
		}

		const csv = '﻿' + lines.join('\r\n')
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		const filename = buildExportFilename(input.title, input.filenameContext, 'csv')
		downloadBlob(blob, filename)
	}

	return {
		exportToPdf,
		exportToCsv,
		describeActiveFilters,
	}
}
